/**
 * Renders an alert's wording from its facts.
 *
 * The backend stores `subjectLabel`, `holderName` and `daysLeft` rather than a finished
 * sentence, because a sentence has a language and the row would otherwise be stuck in
 * whichever one the nightly job happened to run in. This is where it becomes a sentence,
 * in whatever language the reader has chosen.
 *
 * `subjectLabel` itself is NOT translated: it is the document's own name — "DURC",
 * "Idoneità sanitaria", "PES / PAV — CEI 11-27". Those are Italian legal instruments and
 * renaming them would make them unsearchable and unrecognisable to the office.
 */

import { useCallback } from 'react';

import { useI18n } from '@/i18n';
import type { Alert } from './api';

export interface AlertText {
  title: string;
  body: string;
}

export function useAlertText(): (alert: Alert) => AlertText {
  const { t } = useI18n();

  return useCallback(
    (alert) => {
      const label = alert.subjectLabel;

      // Rows written before migration 0003 have no structured fields, only the Italian
      // sentence. Show that rather than nothing — a stale language beats a blank row.
      if (!label) {
        return { title: alert.title ?? alert.kind, body: alert.body ?? '' };
      }

      const subject = alert.holderName
        ? t('alertText.withHolder', { holder: alert.holderName, label })
        : label;

      if (alert.kind === 'document_missing') {
        return { title: t('alertText.missing', { subject }), body: t('alertText.missingBody') };
      }

      const days = alert.daysLeft;

      if (days === null) {
        return { title: t('alertText.expiring', { subject }), body: '' };
      }

      if (days < 0) {
        const ago = Math.abs(days);
        return {
          title: t('alertText.expired', { subject }),
          body: t(ago === 1 ? 'alertText.expiredYesterday' : 'alertText.expiredDaysAgo', {
            count: ago,
          }),
        };
      }

      if (days === 0) {
        return { title: t('alertText.expiresToday', { subject }), body: t('alertText.lastDay') };
      }

      return {
        title: t('alertText.expiring', { subject }),
        body: t(days === 1 ? 'alertText.expiresTomorrow' : 'alertText.expiresInDays', {
          count: days,
        }),
      };
    },
    [t],
  );
}
